import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  actionOpenPartCountMultiplierDialog,
  actionClosePartCountMultiplierDialog,
} from 'lib/actions';
import { PartCountMultiplierDialogEventEmitter } from 'components/PartCountMultiplierDialog';
import { getPromptOperatorForPartCountMultiplier } from 'lib/selectors/getPromptOperatorForPartCountMultiplier';
import { getProductionActivityTypeRefs } from 'lib/selectors/getProductionActivityTypeRefs';

const useRequestPartCountMultiplier = () => {
  const dispatch = useDispatch();
  const isPromptEnabled = useSelector(getPromptOperatorForPartCountMultiplier);
  const productionActivityTypeRefs = useSelector(getProductionActivityTypeRefs);

  const openDialog = useCallback(
    (defaultValue) => {
      return dispatch(actionOpenPartCountMultiplierDialog({ defaultValue }));
    },
    [dispatch]
  );

  const closeDialog = useCallback(() => {
    return dispatch(actionClosePartCountMultiplierDialog());
  }, [dispatch]);

  return useCallback(
    (defaultValue = 1, activityTypeRef) => {
      return new Promise((resolve, reject) => {
        const isProductionActivity = activityTypeRef
          ? productionActivityTypeRefs.includes(activityTypeRef)
          : true;

        if (!isPromptEnabled || !isProductionActivity) {
          resolve(defaultValue);
          return;
        }

        // Event emitter bridges async caller to dialog buttons — same pattern as useRequestJobStartAt.
        // .init() clears prior listeners before each open to prevent stale handlers from a cancelled dialog.
        PartCountMultiplierDialogEventEmitter.init();

        PartCountMultiplierDialogEventEmitter.on('success', (value) => {
          closeDialog();
          resolve(value);
        });

        PartCountMultiplierDialogEventEmitter.on('cancel', () => {
          closeDialog();
          const error = new Error(
            'Part count multiplier selection was cancelled'
          );
          error.code = 'USER_CANCELLED';
          reject(error);
        });

        openDialog(defaultValue);
      });
    },
    [openDialog, closeDialog, isPromptEnabled, productionActivityTypeRefs]
  );
};

export default useRequestPartCountMultiplier;
